import { type ReactNode } from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import type { Move } from '@/schemas/move';
import { cn } from '@/lib/utils';
import { renderInlineMarkdown } from '@/utils/inlineMarkdown';

// molecule — DWR move card: riga espandibile per le mosse — nome + trigger
// inline + chevron per descrizione. Pattern .move-card mockup
// (styles.css 696-771): border 1px rule + bg paper-bright + grid 3-col
// (leading / label / chevron) + hover border-ink + descrizione espansa con
// bordo sinistra.
//
// **Promozione PR 11**: prima `ExpandableMoveRow` page-specific in
// `features/character-builder/sections/`. Promosso a molecule shared dopo
// rule of three onorata (6 callsite: SectionStartingMoves, LevelUpStepMove,
// LevelUpStepGranted, LevelUpStepMulticlass, LevelUpStepMulticlassParts,
// SpellCard). Pattern composition principles §3.1.
//
// **Click area**: container `<label htmlFor>` wrap → click ovunque nella card
// triggera il radio/checkbox del leading slot. Chevron e.preventDefault per
// non far toggle il radio quando l'utente vuole solo espandere/comprimere.
//
// **Prop opzionali level-up** (introdotti PR 8):
//   - `disabled`: opacity 0.45 + cursor-not-allowed (mossa non prendibile)
//   - `meta`: slot pillole .meta-chip sotto il titolo (Richiede / Sostituisce
//     / Reason — mockup levelup.jsx riga 315-328 + styles.css 1991-2002)
//   - `multi`: badge .multi-badge inline al titolo per mosse multiclasse
//     (mockup styles.css 2009-2016)
// Default omitted → comportamento builder PR 6 invariato.
//
// **PR feature/hub-mobile-complete (FI-59 closure)**: nuovo slot `setupSlot`
// renderizzato DENTRO il body espanso, sotto la description con separatore.
// Usato da `<FullSheet>` MovesSection per mostrare il setup compilato della
// mossa (selectedChoices / choiceGroups / narrativeFields / recruitOutcome)
// inline alla card invece che come sibling sotto.
//
// **Slot**:
// - `leading`: contenuto a sinistra del label (radio/checkbox shadcn o niente).
//   Su mossa "auto" il leading è null e mostriamo un badge "AUTO" inline.
// - `setupComplete`: se `true`, mostra icona Check ink accanto al nome
//   (mossa con setup compilato — FI-18 / FI-21).
// - `setupSlot`: contenuto da mostrare nel body espanso sotto la description.

export interface MoveCardProps {
  move: Move | undefined;
  fallbackId: string;
  expanded: boolean;
  onToggle: () => void;
  leading?: ReactNode;
  labelHtmlFor?: string;
  setupComplete?: boolean;
  trailing?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  meta?: ReactNode;
  multi?: boolean;
  setupSlot?: ReactNode;
  /**
   * Se `true`, il chevron toggle è sempre visibile anche quando la mossa
   * non ha description: utile per mosse senza description ma con `setupSlot`
   * (es. FullSheet Hub mosse con setup compilato senza description definita).
   */
  forceExpandable?: boolean;
}

const ROW_BASE_CLASS =
  'border-rule bg-paper-bright grid cursor-pointer grid-cols-[auto_1fr_auto] items-start gap-[0.85rem] border p-3 transition-[border-color,background-color] duration-150 hover:border-ink';

export function MoveCard(props: MoveCardProps) {
  const labelHtmlFor = props.labelHtmlFor;
  const selected = props.selected ?? false;
  const disabled = props.disabled ?? false;
  return (
    <RowShell labelHtmlFor={labelHtmlFor} selected={selected} disabled={disabled}>
      <MoveCardInner {...props} />
    </RowShell>
  );
}

function MoveCardInner(props: MoveCardProps) {
  const description = props.move?.description;
  const expandable = isExpandable(props);
  const setupComplete = props.setupComplete ?? false;
  // FI-75: setup pending = la card ha un setupSlot attivo ma il setup non è
  // ancora completo. Mostra un badge "DA COMPILARE" inline al titolo per
  // segnalare che c'è una scelta da fare anche con la card compressa.
  const setupPending = props.setupSlot !== null && props.setupSlot !== undefined && !setupComplete;
  return (
    <>
      <LeadingSlot>{props.leading}</LeadingSlot>
      <div className="flex flex-col gap-1">
        <MoveLabel
          name={props.move?.name ?? props.fallbackId}
          trigger={props.move?.trigger}
          setupComplete={setupComplete}
          setupPending={setupPending}
          trailing={props.trailing}
          multi={props.multi ?? false}
        />
        {props.meta ? (
          <div className="mt-[0.35rem] flex flex-wrap gap-[0.35rem]">{props.meta}</div>
        ) : null}
      </div>
      <ExpandToggle expanded={props.expanded} onToggle={props.onToggle} expandable={expandable} />
      <ExpandedBody
        expanded={props.expanded}
        description={description}
        setupSlot={props.setupSlot}
      />
    </>
  );
}

function isExpandable(props: MoveCardProps): boolean {
  if (props.forceExpandable === true) return true;
  if (props.setupSlot !== undefined) return true;
  if (props.move?.description !== undefined) return true;
  return false;
}

// Container: <label htmlFor={labelHtmlFor}> se c'è un input collegato (radio/
// checkbox del leading slot); fallback <div> per mosse auto-grant senza input.
// Cliccare ovunque dentro il label triggera l'input collegato (browser native).
function RowShell({
  labelHtmlFor,
  selected,
  disabled,
  children,
}: {
  labelHtmlFor: string | undefined;
  selected: boolean;
  disabled: boolean;
  children: ReactNode;
}) {
  const className = cn(
    ROW_BASE_CLASS,
    selected && 'border-ink bg-paper-soft',
    !labelHtmlFor && 'cursor-default',
    disabled && 'pointer-events-none cursor-not-allowed opacity-45',
  );
  const dataSelected = selected ? 'true' : undefined;
  const dataDisabled = disabled ? 'true' : undefined;
  if (labelHtmlFor && !disabled) {
    return (
      <label htmlFor={labelHtmlFor} data-selected={dataSelected} className={className}>
        {children}
      </label>
    );
  }
  return (
    <div data-selected={dataSelected} data-disabled={dataDisabled} className={className}>
      {children}
    </div>
  );
}

function ExpandedBody({
  expanded,
  description,
  setupSlot,
}: {
  expanded: boolean;
  description: string | undefined;
  setupSlot: ReactNode;
}) {
  if (!expanded) return null;
  if (!description && !setupSlot) return null;
  return (
    <div className="col-span-3 flex flex-col gap-2">
      {description ? <ExpandedDescription text={description} /> : null}
      {setupSlot ? (
        <div className="border-rule font-serif border-l-2 pl-[0.85rem]">{setupSlot}</div>
      ) : null}
    </div>
  );
}

function LeadingSlot({ children }: { children: ReactNode }) {
  if (!children) return <span aria-hidden />;
  return <div className="mt-[0.15em] flex-shrink-0">{children}</div>;
}

function MoveLabel({
  name,
  trigger,
  setupComplete,
  setupPending,
  trailing,
  multi,
}: {
  name: string;
  trigger: string | undefined;
  setupComplete: boolean;
  setupPending: boolean;
  trailing: ReactNode;
  multi: boolean;
}) {
  return (
    <div className="font-display flex flex-wrap items-center gap-1.5 text-[0.98rem] font-bold tracking-[0.06em] uppercase">
      <span>{name}</span>
      {multi ? (
        <span className="bg-ink-soft text-paper-bright font-display inline-block px-[0.45em] py-[0.1em] text-[0.6rem] tracking-[0.1em] uppercase">
          multi
        </span>
      ) : null}
      {setupPending ? (
        <span
          aria-label="Setup da compilare"
          className="bg-accent-dwr/10 border-accent-dwr text-accent-dwr font-display inline-block border px-[0.45em] py-[0.1em] text-[0.6rem] tracking-[0.1em] uppercase"
        >
          da compilare
        </span>
      ) : null}
      {setupComplete ? (
        <Check aria-label="Setup completo" className="text-ink h-4 w-4" strokeWidth={2.5} />
      ) : null}
      {trigger ? (
        <em className="font-serif text-ink-soft ml-1 text-[0.85em] font-normal tracking-normal normal-case">
          — {renderInlineMarkdown(trigger)}
        </em>
      ) : null}
      {trailing}
    </div>
  );
}

function ExpandToggle({
  expanded,
  onToggle,
  expandable,
}: {
  expanded: boolean;
  onToggle: () => void;
  expandable: boolean;
}) {
  if (!expandable) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        // preventDefault: evita che il <label> wrapping triggeri il radio/checkbox
        // collegato (htmlFor) come effetto collaterale del click sul chevron.
        // stopPropagation: evita bubble verso il container in caso di onClick.
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      aria-label={expanded ? 'Comprimi descrizione' : 'Espandi descrizione'}
      className="text-ink-soft hover:text-ink grid h-7 w-7 shrink-0 cursor-pointer place-items-center bg-transparent transition-colors"
    >
      {expanded ? (
        <ChevronUp aria-hidden className="h-4 w-4" strokeWidth={2.2} />
      ) : (
        <ChevronDown aria-hidden className="h-4 w-4" strokeWidth={2.2} />
      )}
    </button>
  );
}

function ExpandedDescription({ text }: { text: string }) {
  return (
    <div className="border-rule font-serif text-ink-soft mt-2 border-l-2 pl-[0.85rem] text-[0.92rem] leading-relaxed whitespace-pre-line">
      {renderInlineMarkdown(text)}
    </div>
  );
}
